/**
 * Customisation bar: saved views (pick, save, save as, rename, set default, delete via /views), report language
 * (EN/AR), hide/show sections, print view. Viewers can pick views but not change them.
 */
import { useState } from 'react';
import { REPORT_SECTIONS } from '@qm/contracts/audit-remediation';
import { Button, Select, TextInput } from '@qm/ui';
import type { Loc } from './model';
import type { ReportStore, ReportUiState } from './store';
import { useReportText } from './text';

type Mode = null | 'create' | 'rename' | 'delete';

export function ViewsBar({ state, store, canEdit, onPrint }: { state: ReportUiState; store: ReportStore; canEdit: boolean; onPrint: () => void }) {
  const { t } = useReportText();
  const [mode, setMode] = useState<Mode>(null);
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const views = state.views.status === 'ready' ? state.views.data : [];
  const active = store.activeView;
  const dirty = store.dirty;
  const busy = state.busy === 'view';

  const open = (m: Mode) => { setMode(m); setErr(null); setName(m === 'rename' ? active?.name ?? '' : ''); setMakeDefault(false); };
  const submit = async () => {
    const r = mode === 'create' ? await store.createView(name, makeDefault) : mode === 'rename' && active ? await store.renameView(active.id, name) : mode === 'delete' && active ? await store.deleteView(active.id) : null;
    if (!r) return;
    if (r.ok) { setMode(null); setErr(null); } else setErr(r.errorKey);
  };

  return (
    <section className="qm-ar-views qm-ar-noprint" aria-labelledby="ar-views-title">
      <h2 id="ar-views-title" className="qm-sr-only">{t('audit-remediation.report.views.title')}</h2>
      <div className="qm-ar-views__row">
        <div className="qm-ar-toolbar__field">
          <label className="qm-field__label" htmlFor="ar-view">{t('audit-remediation.report.views.pick')}</label>
          <Select id="ar-view" value={state.activeViewId ?? ''} onChange={(e) => store.applyView(e.currentTarget.value || null)}>
            <option value="">{t('audit-remediation.report.views.standard')}</option>
            {views.map((v) => <option key={v.id} value={v.id}>{v.isDefault ? t('audit-remediation.report.views.defaultName', { name: v.name }) : v.name}</option>)}
          </Select>
        </div>
        {dirty ? <span className="qm-ar-muted" role="status">{t('audit-remediation.report.views.unsaved')}</span> : null}
        {canEdit ? (
          <div className="qm-ar-inline">
            {active && dirty ? <Button size="sm" variant="primary" disabled={busy} onClick={() => void store.saveActiveView()}>{t('audit-remediation.views.save')}</Button> : null}
            <Button size="sm" disabled={busy} onClick={() => open('create')}>{t('audit-remediation.report.views.saveAs')}</Button>
            {active ? <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => open('rename')}>{t('audit-remediation.report.views.rename')}</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void store.setDefaultView(active.id, !active.isDefault)}>
                {t(active.isDefault ? 'audit-remediation.report.views.unsetDefault' : 'audit-remediation.report.views.setDefault')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => open('delete')}>{t('audit-remediation.views.delete')}</Button>
            </> : null}
          </div>
        ) : null}
      </div>

      {mode ? (
        <form className="qm-ar-viewform" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-labelledby="ar-viewform-title">
          <h3 id="ar-viewform-title" className="qm-ar-h3">{t(`audit-remediation.report.views.form.${mode}`, { name: active?.name ?? '' })}</h3>
          {mode !== 'delete' ? (
            <div className="qm-field" data-invalid={err ? 'true' : undefined}>
              <label className="qm-field__label" htmlFor="ar-view-name">{t('audit-remediation.report.views.name')}</label>
              <TextInput id="ar-view-name" value={name} maxLength={80} required autoFocus aria-invalid={err ? true : undefined}
                aria-describedby={err ? 'ar-view-err' : undefined} onChange={(e) => setName(e.currentTarget.value)} />
            </div>
          ) : null}
          {mode === 'create' ? (
            <label className="qm-ar-check" htmlFor="ar-view-default">
              <input id="ar-view-default" type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.currentTarget.checked)} />
              {t('audit-remediation.report.views.makeDefault')}
            </label>
          ) : null}
          {err ? <p className="qm-field__error" id="ar-view-err" role="alert">{t(err)}</p> : null}
          <div className="qm-ar-inline">
            <Button type="submit" size="sm" variant={mode === 'delete' ? 'ink' : 'primary'} disabled={busy}>{t(`audit-remediation.report.views.confirm.${mode}`)}</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>{t('audit-remediation.report.cancel')}</Button>
          </div>
        </form>
      ) : null}

      <div className="qm-ar-views__row">
        <div className="qm-ar-toolbar__field">
          <label className="qm-field__label" htmlFor="ar-lang">{t('audit-remediation.report.language')}</label>
          <Select id="ar-lang" value={state.contentLocale} onChange={(e) => store.setContentLocale(e.currentTarget.value as Loc)}>
            <option value="en" lang="en">{t('audit-remediation.report.lang.en')}</option>
            <option value="ar" lang="ar">{t('audit-remediation.report.lang.ar')}</option>
          </Select>
        </div>
        <details className="qm-ar-pop">
          <summary>{t('audit-remediation.report.sections')}</summary>
          <fieldset className="qm-ar-pop__body">
            <legend className="qm-sr-only">{t('audit-remediation.report.sections')}</legend>
            {REPORT_SECTIONS.map((s) => {
              const id = `ar-sec-${s}`;
              return (
                <label key={s} htmlFor={id} className="qm-ar-check">
                  <input id={id} type="checkbox" checked={!state.def.hiddenSections.includes(s)} onChange={() => store.toggleSection(s)} />
                  {t(`audit-remediation.report.section.${s}`)}
                </label>
              );
            })}
          </fieldset>
        </details>
        <Button size="sm" onClick={onPrint}>{t('audit-remediation.report.print')}</Button>
      </div>
    </section>
  );
}
